import { Locale } from '@headless-workspace/config';
import { tailwindTheme, TailwindTheme } from '@headless-workspace/theme';
import { specialCharsMap } from './SpecialCharsMap';

const addSpaces = (str: string, leftSpaces: number, rightSpaces?: number): string => {
    return ' '.repeat(leftSpaces) + str + ' '.repeat(rightSpaces ?? 0);
};

const capitalize = (str: string): string => {
    return str && str.length > 0 ? str.charAt(0).toUpperCase() + str.slice(1) : '';
};

type FormatPriceParams = {
    price?: number;
    currency: string;
    locale: Locale;
    isInteger?: boolean;
    defaultValue?: '' | number | null;
};

/**
 * Formats a price according to the specified locale and currency.
 * @param price - The price to format, can be a number or undefined.
 * @param currency - The currency code (e.g., 'USD', 'EUR').
 * @param locale - The locale to use for formatting (e.g., 'fr-FR', 'de-DE').
 * @param isInteger - If true, formats the price as an integer (no decimal places).
 * @param defaultValue - The default value to return if the price is undefined. It can be a string, number, or null. If null, returns undefined.
 */
const formatPrice = ({
    price,
    currency,
    locale,
    isInteger = false,
    defaultValue = '',
}: FormatPriceParams): string | undefined => {
    const options: Intl.NumberFormatOptions = {
        style: 'currency',
        currency,
        minimumFractionDigits: isInteger ? 0 : 2,
    };

    if (typeof price === 'number') {
        return new Intl.NumberFormat(locale, options).format(price);
    }

    if (typeof defaultValue === 'number') {
        return new Intl.NumberFormat(locale, options).format(defaultValue);
    }

    if (defaultValue === null) {
        return undefined;
    }
    return defaultValue;
};

const formatPriceWithConditions = (
    price: number | undefined,
    currency: string,
    locale: Locale,
    condition: boolean,
    quantity = 1,
): string | undefined => {
    if (!price) {
        return undefined;
    }

    const finalPrice = price * quantity;
    const formattedPrice = formatPrice({ price: finalPrice, currency, locale });

    return condition ? addAsterisk(formattedPrice) : formattedPrice;
};

const addPrefix = (str: string, prefix: string, condition?: boolean): string => {
    return condition ? prefix + str : str;
};

const addSuffix = (str: string, suffix: string, condition?: boolean): string => {
    return condition ? str + suffix : str;
};

const decodeSpecialChars = (str: string): string => {
    return str.replace(/(&[^;]+;)/g, (char: string) => specialCharsMap[char]);
};

const replaceQuotes = (str: string): string => str.replace(/[’‘]/g, "'");

const makeMediaQuery = (breakpoint: keyof TailwindTheme['screens'], max = false): string => {
    return `(${
        max
            ? `max-width: calc(${tailwindTheme.screens[breakpoint]} - 1px)`
            : `min-width: ${tailwindTheme.screens[breakpoint]}`
    })`;
};

const removeTrailingSpaces = (str: string): string => {
    return str.trimEnd().replace(/[\u00A0\u200B-\u200D\uFEFF]+$/g, '');
};

const isUrl = (str?: string): boolean => {
    if (!str) {
        return false;
    }

    const urlPattern = /^https?:\/\/.*$/i;
    return urlPattern.test(str);
};

const isTrue = (str: string): boolean => {
    return str.toLowerCase() === 'true';
};

const validateEmail = (email: string): boolean => {
    const emailPattern =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return emailPattern.test(email?.toLowerCase());
};

const isNotEmpty = (value?: string): boolean => {
    return !!value;
};

export type Device = 'Android' | 'iPhone' | 'iPad';

const isDevice = (devices: Device | Device[]): boolean => {
    const deviceList = Array.isArray(devices) ? devices : [devices];

    const userAgent = typeof window !== 'undefined' ? window.navigator?.userAgent : null;
    if (!userAgent) return false;

    return deviceList.some((device) => new RegExp(device, 'i').test(userAgent));
};

const removeOuterHTMLTag = (htmlString: string): string => {
    if (!htmlString) return htmlString;

    const trimmed = htmlString.trim();

    if (!trimmed.startsWith('<') || !trimmed.endsWith('>')) return htmlString;

    const tagStart = trimmed.indexOf('<');
    const tagEnd = trimmed.indexOf('>');

    if (tagStart === -1 || tagEnd === -1) return htmlString;

    const tagName = trimmed.slice(tagStart + 1, tagEnd).split(' ')[0];

    const closingTag = `</${tagName}>`;
    const closingTagIndex = trimmed.lastIndexOf(closingTag);

    if (closingTagIndex !== -1 && closingTagIndex + closingTag.length === trimmed.length) {
        return trimmed.slice(tagEnd + 1, closingTagIndex);
    }

    return htmlString;
};

const removeSpaceAndLowercase = (str: string) => str.replace(/\s+/g, '').toLowerCase();

const stripTags = (text: string) => {
    const doc = new DOMParser().parseFromString(text, 'text/html');
    return doc.body.textContent || '';
};

const removeAsterisk = (text: string): string => text.replace(/\*/g, '');

const addAsterisk = (text?: string): string => {
    if (!text) {
        return '';
    }
    return text.endsWith('*') ? text : `${text}*`;
};

const getClampedContent = (content: string, maxLength: number, isClamped: boolean) => {
    if (!isClamped || content.length <= maxLength) {
        return content;
    }
    return `${content.slice(0, maxLength)}...`;
};

const sanitizeHTML = (str: string): string => {
    return str.replace(/<\/?([a-zA-Z]{1,7})(?=\s|\/?>)[^>]*>/g, '').trim();
};

const truncate = (str: string, limit: number): string => {
    return str.slice(0, limit);
};

const trim = (str?: string): string | undefined => {
    return str?.trim() || undefined;
};

export const StringHelpers = {
    addSpaces,
    capitalize,
    formatPrice,
    formatPriceWithConditions,
    addPrefix,
    addSuffix,
    decodeSpecialChars,
    replaceQuotes,
    makeMediaQuery,
    removeTrailingSpaces,
    isUrl,
    isTrue,
    validateEmail,
    isNotEmpty,
    isDevice,
    removeOuterHTMLTag,
    removeSpaceAndLowercase,
    stripTags,
    removeAsterisk,
    addAsterisk,
    getClampedContent,
    sanitizeHTML,
    truncate,
    trim,
};
